import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal } from "lucide-react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { invoiceApi } from "../InvoiceApi";

import { fallbackName } from "../Utils/util";
import { useDispatch, useSelector } from "react-redux";
import { setListViewState, addTab } from "../../../../redux/slices/invoiceSlice";
import { usePrivilege } from "../../../../guard/usePrivilege";
import GlobalPagination from "../../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import { statusCls, auditCls, formatMoney, useInvoiceListData } from "./invoiceShared";
import { buildFilterNode, countActiveFilters } from "../../../AdvancedFilter/filterTree";
import { AdvancedFilterToolbar } from "../../../AdvancedFilter/AdvancedFilterToolbar";
import { AdvancedFilterModal } from "../../../AdvancedFilter/AdvancedFilterModal";
import { SavedFiltersControl } from "../../../AdvancedFilter/SavedFiltersControl";
import { INVOICE_RULE_FIELDS } from "../invoicesFilterFields";

const SORTABLE_COLUMNS = {
  "Invoice Number":        "invoiceNumber",
  "Invoice Date":          "invoiceDate",
  "Invoice Total Charges": "total",
  "Invoice Status":        "invoiceStatus",
  "Invoice Audit Status":  "auditStatus",
  "Payment Terms":         "paymentTerms",
  "Pay To":                "payToCompanyName",
  "Bill To":               "billToCompanyName",
};


const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Invoice Number",
  "Invoice Date",
  "Invoice Total Charges",
  "Invoice Status",
  "Invoice Audit Status",
  "Payment Terms",
  "Pay To",
  "Bill To",
];

export default function RejectedInvoicesTab({
  onInvoiceClick,
  searchQuery = "",
  refreshKey,
  stateKey = "rejected-invoices",
  isSearchTab = false,
}) {
  const auditRefreshKey = useSelector((s) => s.invoices.auditRefreshKey);
  const dispatch        = useDispatch();
  const listViewState   = useSelector((s) => s.invoices.listViewState || {});
  const savedView       = listViewState[stateKey] || {};

  const [page,       setPage]      = useState(savedView.currentPage  || 1);
  const [size,       setSize]      = useState(savedView.itemsPerPage || 50);
  const [sortBy,     setSortBy]    = useState(savedView.sortBy ?? "invoiceDate");
  const [ascending,  setAscending] = useState(savedView.ascending ?? false);
  const [openMenu,   setOpenMenu]  = useState(null);
  const [selected,   setSelected]  = useState([]);
  const [columnWidths, setColumnWidths] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({ rules: [], matchType: "AND" });
  const [showFilterModal, setShowFilterModal] = useState(false);

  const dropdownRef       = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const checkboxHeaderRef = useRef(null);

  const { hasPrivilege } = usePrivilege();
  const canCreate  = hasPrivilege("invoices:CREATE");

  const tableViews = useTableSavedViews({
    pageKey: stateKey || TABLE_PAGE_KEYS.INVOICE_REJECTED,
    allHeaders: TABLE_HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Invoice Number"],
    getExtraConfig: () => ({ currentPage: page, pageSize: size }),
    onApplyConfig: (config) => {
      if (config.currentPage) setPage(config.currentPage);
      if (config.pageSize) setSize(config.pageSize);
    },
  });

  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  useEffect(() => {
    dispatch(setListViewState({ stateKey, currentPage: page, itemsPerPage: size, sortBy, ascending }));
  }, [dispatch, stateKey, page, size, sortBy, ascending, auditRefreshKey]);

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleSort = (newSortBy) => {
    setPage(1);
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  // Memoized: an unmemoized recompute creates a new object reference every render, which
  // retriggers useInvoiceListData's fetch effect in an infinite loop (see MyinvoiceTab.js).
  const filterNode = useMemo(
    () => buildFilterNode(INVOICE_RULE_FIELDS, advancedFilters),
    [advancedFilters]
  );

  const {
    invoices,
    totalPages,
    showLoadingOverlay,
    errorMessage,
  } = useInvoiceListData({
    invoiceStatus: "Rejected",
    searchQuery,
    page,
    size,
    refreshKey,
    sortBy,
    ascending,
    onPageOutOfRange: setPage,
    filter: filterNode,
  });

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const rows = invoices.map((inv) => ({
    Selection:               inv.uniqueId,
    Action:                  inv,
    "Invoice Number":        inv.invoiceNumber,
    "Invoice Date":          inv.invoiceDate,
    "Invoice Total Charges": formatMoney(inv.total, inv.currency),
    "Invoice Status": (
      <span className={`px-2 py-1 rounded font-medium ${statusCls(inv.invoiceStatus)}`}>
        {(inv.invoiceStatus || "REJECTED").toUpperCase()}
      </span>
    ),
    "Invoice Audit Status": (
      <span className={`px-2 py-1 rounded font-medium ${auditCls(inv.auditStatus)}`}>
        {(inv.auditStatus || "-").toUpperCase()}
      </span>
    ),
    "Payment Terms": inv.paymentTerms,
    "Pay To":        inv.payToCompanyName,
    "Bill To":       fallbackName(inv.billToCompanyName || ""),
    __actions: [
      { icon: "Eye", label: "View", color: "blue", action: () => onInvoiceClick(inv) },
      ...(canCreate ? [{
        icon: "Edit", label: "Resume", color: "blue",
        action: () => dispatch(addTab({ name: `Edit (${inv.invoiceNumber})`, type: "new", data: inv })),
      }] : []),
    ],
  }));

  const toggleSelectAll = (checked) =>
    setSelected(checked ? rows.map((r) => r.Selection) : []);

  if (showLoadingOverlay) {
    return (
      <Loader
        message="Loading Rejected Invoices…"
        size="h-5 w-5"
        color="text-blue-600 dark:text-acs-dark-text"
      />
    );
  }

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
      {/* Error banner — surfaces fetch failures instead of silently showing 0 rejected invoices */}
      {errorMessage && (
        <div className="shrink-0 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {errorMessage}
        </div>
      )}
      {!isSearchTab && (
        <AdvancedFilterToolbar
          ruleFields={INVOICE_RULE_FIELDS}
          filters={advancedFilters}
          activeCount={countActiveFilters(INVOICE_RULE_FIELDS, advancedFilters)}
          onOpenModal={() => setShowFilterModal(true)}
          onClearAll={() => setAdvancedFilters({ rules: [], matchType: "AND" })}
          onChangeFilters={setAdvancedFilters}
          trailing={
            <div className="flex items-center gap-2">
              <SavedFiltersControl
                pageKey={stateKey || TABLE_PAGE_KEYS.INVOICE_REJECTED}
                ruleFields={INVOICE_RULE_FIELDS}
                currentFilters={advancedFilters}
                onApply={setAdvancedFilters}
              />
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={TABLE_HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          }
        />
      )}

      {isSearchTab && (
            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={TABLE_HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          )}
{showFilterModal && (
        <AdvancedFilterModal
          ruleFields={INVOICE_RULE_FIELDS}
          filters={advancedFilters}
          onApply={setAdvancedFilters}
          onClose={() => setShowFilterModal(false)}
        />
      )}
      <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
        <Table
          {...columnManagerProps}
          data={rows}
          initialColumnWidths={columnWidths}
          onColumnResize={setColumnWidths}
          enableColumnResize
          enableColumnReorder
          enableColumnPin
          selectedRows={selected}
          totalValidRows={rows.length}
          onSelectAll={toggleSelectAll}
          renderCheckboxActions={() => {}}
          checkboxHeaderRef={checkboxHeaderRef}
          sortBy={sortBy}
          ascending={ascending}
          onSort={onHeaderSortClick}
          sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
          sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}

        >
          {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
            const id      = row.Selection;
            const isOpen  = openMenu === id;
            const orderedDataHeaders = (visibleColumns ?? [])
              .map((col) => col.id)
              .filter((id) => id !== "Selection" && id !== "Action");
            const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

            return (
              <>
                {/* Column 0 — Checkbox */}
                <td
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                  style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                >
                  <input
                    type="checkbox"
                    className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                    checked={selected.includes(id)}
                    onChange={() =>
                      setSelected((s) =>
                        s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
                      )
                    }
                  />
                </td>

                {/* Column 1 — Action (portal dropdown) */}
                <td
                  onClick={(e) => e.stopPropagation()}
                  className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                  style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                >
                  <button
                    data-action-trigger
                    className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                    onClick={(e) => handleActionClick(id, e)}
                  >
                    <MoreHorizontal size={18} className="mx-auto" />
                  </button>
                </td>

                {isOpen && ReactDOM.createPortal(
                  <div
                    ref={dropdownRef}
                    style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                  >
                    <ActionMenu
                      rowId={id}
                      isOpen
                      moduleName="Invoice"
                      onClose={() => setOpenMenu(null)}
                      actions={row.__actions}
                    />
                  </div>,
                  document.body
                )}

                {/* Columns 2+ — Data */}
                {dataHeadersToRender.map((col) => {
                  return (
                    <td
                      key={col}
                      className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(col).className}`}
                      style={{
                        ...getStickyCellProps(col).style,
                        whiteSpace:   "nowrap",
                        overflow:     "hidden",
                        textOverflow: "ellipsis",
                        width:        "200px",
                        minWidth:     "200px",
                        maxWidth:     "200px",
                      }}
                      onDoubleClick={() => {
                        const inv = row.Action;
                        canCreate
                          ? dispatch(addTab({ name: `Edit (${inv.invoiceNumber})`, type: "new", data: inv }))
                          : onInvoiceClick(inv);
                      }}
                    >
                      {row[col]}
                    </td>
                  );
                })}
              </>
            );
          }}
        </Table>
      </div>

      <GlobalPagination
        label="Invoices per page"
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={size}
        onItemsPerPageChange={(v) => { setSize(v); setPage(1); }}
      />
    </div>
  );
}